import React, { useState, useCallback } from 'react';
import FileUploadSVG from "../../assets/svg/file-upload.svg?react";
import DeleteFilledSVG from "../../assets/svg/delete-filled.svg?react";

interface FileDragAndDropProps {
  fileDescription?: string;
  fileSubject?: string;
  uploadFiles: React.Dispatch<React.SetStateAction<File[]>>;
  fileData: File[];
  deleteFile?: () => void;
  multiple?: boolean;
}

const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".webp"];
const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

export default function FileDragAndDrop({ uploadFiles, fileData, deleteFile, fileDescription, fileSubject, multiple = false }: FileDragAndDropProps) {
  const [isDragging, setIsDragging] = useState(false);

  // 🎯 파일 검증 및 상태 업데이트 로직 (공통)
  const processFiles = useCallback((files: File[]) => {
    const validFiles = files.filter((file) => {
      const fileName = file.name.toLowerCase();
      const isCorrectExtension = ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
      const isUnderSize = file.size <= MAX_FILE_SIZE;
      return isCorrectExtension && isUnderSize;
    });

    if (validFiles.length !== files.length) {
      alert("JPG, PNG, PDF 형식의 파일만 업로드 가능해요. (최대 용량 3MB)");
    }

    if (validFiles.length > 0) {
      if (!multiple) {
        // 단일 모드: 항상 새 파일 하나로 교체
        uploadFiles([validFiles[0]]);
      } else {
        // 다중 모드: 기존 파일 리스트 뒤에 추가
        uploadFiles((prev) => [...prev, ...validFiles]);
      }
    }
  }, [multiple, uploadFiles]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // 🎯 드롭 핸들러에 검증 로직 적용
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  }, [processFiles]);

  return (
    <div className="w-full mx-auto space-y-2">
      {fileSubject ? <p className='px-1 text-[12px] font-normal text-black/60'>{fileSubject}</p> : null}
      {!multiple && fileData.length > 0 ? null : (
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
          relative border-2 border-dashed rounded-xl px-4 py-6 transition-all duration-200 space-y-2
          flex flex-col items-center justify-center cursor-pointer
          ${isDragging
              ? "border-blue-500 bg-white scale-[1.02]"
              : "border-black/15 bg-white hover:border-black/30"}
        `}
        >
          <FileUploadSVG />
          <span className='text-[16px]'>{!!fileDescription ? fileDescription : (<><span className='text-primary-50 underline'>Link</span>or drag and drop</>)}</span>
          <p className="text-[14px] font-normal text-black/60">PNG, JPG or PDF (max. 3MB)</p>

          <input
            type="file"
            multiple={multiple}
            accept={ALLOWED_EXTENSIONS.join(", ")}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            onChange={(e) => {
              if (e.target.files) {
                const selectedFiles = Array.from(e.target.files);
                processFiles(selectedFiles);
                e.target.value = ""; // 동일 파일 재선택 가능하게 초기화
              }
            }}
          />
        </div>
      )}

      {fileData?.map((file, idx) => {
        const fileSizeKB = file.size / 1024;
        const maxSizeKB = 3 * 1024;
        const progress = Math.min((fileSizeKB / maxSizeKB) * 100, 100);
        const isOverSize = fileSizeKB > maxSizeKB;

        return (
          <React.Fragment key={`${file.name}-${idx}`}>
            {!multiple ? (
              <div className='flex-row gap-2'>
                <span className='text-[14px] font-medium'>{file.name}</span>
                <span className='text-primary-50 text-[13px] font-bold cursor-pointer'
                  onClick={() => uploadFiles((prev) => prev.filter((_, i) => i !== idx))}
                >파일 변경</span>
              </div>
            ) : (
              <div className="bg-white flex flex-row justify-between items-center gap-4 p-4 border-b">
                <FileUploadSVG className='w-[24px] h-[24px] shrink-0' />
                <div className='flex-1 flex flex-col gap-1'>
                  <span className="text-[16px] font-medium truncate">{file.name}</span>
                  <span className="text-[14px] text-black/60">{fileSizeKB.toFixed(1)} KB</span>
                  <div className="w-[160px] h-2 bg-blue-300 rounded-full overflow-hidden mt-1">
                    <div
                      className={`h-full transition-all duration-300 ${isOverSize ? 'bg-red-500' : 'bg-blue-500'}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
                <DeleteFilledSVG
                  className="cursor-pointer"
                  onClick={() => {
                    uploadFiles((prev) => prev.filter((_, i) => i !== idx));
                    deleteFile?.();
                  }}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}